import React from 'react';
import PropTypes from 'prop-types';
import { Accordion, AccordionSummary, AccordionDetails, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

/**
 * Reusable foldable section. Visually matches the page-internal section
 * dividers used elsewhere in the portal: outlined, no shadow, no MUI default
 * top-rule, with a chevron expand icon.
 *
 *   <Foldable header="Fan-out: process many files in parallel">
 *     ...long explainer...
 *   </Foldable>
 *
 * `header` is the visible label. Pass a string (rendered as a `Typography
 * variant="h6"`, matching the in-card section headings used in the rest of
 * the app) or any node for full control. `defaultOpen` defaults to false —
 * the component is intended for "skip past unless interested" content.
 *
 * `sx` is merged onto the outer Accordion so callers can override spacing
 * (`mt`, `mb`) without redefining the visual baseline.
 */
export default function Foldable({ header, defaultOpen, children, sx }) {
    const headerNode = typeof header === 'string'
        ? <Typography variant='h6'>{header}</Typography>
        : header;

    return (
        <Accordion
            defaultExpanded={defaultOpen}
            disableGutters
            sx={{
                mt: 3,
                '&:before': { display: 'none' },
                boxShadow: 'none',
                border: 1,
                borderColor: 'divider',
                ...sx,
            }}
            >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                {headerNode}
            </AccordionSummary>
            <AccordionDetails>
                {children}
            </AccordionDetails>
        </Accordion>
    );
}

Foldable.propTypes = {
    header: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
    defaultOpen: PropTypes.bool,
    children: PropTypes.node,
    sx: PropTypes.object,
};

Foldable.defaultProps = {
    defaultOpen: false,
};
